import { CUSTOM_RANGE_YEARS, RANGE_PRESETS } from './analytics';
import { formatDisplayDate } from './dateDisplay';

/**
 * A chart's date range (keepr-api SCHEMA "Charts" › the spec's `time.range`;
 * plan §4.4): a preset, a custom span of days, or none. Ranges are days in
 * the COLLECTION's zone, worked out by the server; the web only names them
 * and carries them.
 *
 *   { preset: 'last30d' }                      one of the vendored RANGE_PRESETS
 *   { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }   both days included, 1900–2200
 *   null                                       no range (the chart's own: none)
 *
 * The chart page's range control replaces a chart's saved range for one visit
 * (plan §6.3). Its choice lives in the URL, never saved: `?range=<preset>` or
 * `?from=YYYY-MM-DD&to=YYYY-MM-DD`; with neither, the chart's own range runs.
 */

/** The words for each preset, in the vendored order. */
export const RANGE_PRESET_LABELS = Object.freeze({
	today: 'Today',
	last7d: 'Last 7 days',
	last30d: 'Last 30 days',
	last90d: 'Last 90 days',
	last12m: 'Last 12 months',
	thisWeek: 'This week',
	thisMonth: 'This month',
	thisQuarter: 'This quarter',
	thisYear: 'This year',
	lastMonth: 'Last month',
	lastYear: 'Last year',
	all: 'All time',
});

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const isPreset = (value) => typeof value === 'string' && RANGE_PRESETS.includes(value);

/** A calendar day the server takes: YYYY-MM-DD, a real day, within the custom range's years. */
export function isRangeDay(value) {
	const m = typeof value === 'string' ? DAY.exec(value) : null;
	if (!m) return false;
	const [year, month, date] = [Number(m[1]), Number(m[2]), Number(m[3])];
	if (year < CUSTOM_RANGE_YEARS.min || year > CUSTOM_RANGE_YEARS.max) return false;
	// A real day: Feb 30 rolls over, so it does not read back as itself.
	const at = new Date(Date.UTC(year, month - 1, date));
	return at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === date;
}

/** A range as the server takes it, or undefined when the value is not one. */
export function normalizeRange(value) {
	if (value === null) return null;
	if (!value || typeof value !== 'object') return undefined;
	if (isPreset(value.preset)) return { preset: value.preset };
	if (isRangeDay(value.from) && isRangeDay(value.to) && value.from <= value.to) return { from: value.from, to: value.to };
	return undefined;
}

/**
 * The override a URL carries: `{ preset }`, `{ from, to }`, or undefined for
 * "the chart's own range". Anything malformed is no override, never a
 * request the server would refuse.
 */
export function rangeFromSearch(searchParams) {
	const get = (key) => (searchParams && typeof searchParams.get === 'function' ? searchParams.get(key) : null);
	const preset = get('range');
	if (preset !== null) return isPreset(preset) ? { preset } : undefined;
	const from = get('from');
	const to = get('to');
	if (from !== null || to !== null) return normalizeRange({ from, to });
	return undefined;
}

/** Writes an override into a copy of the URL's params (undefined clears it). */
export function rangeToSearch(searchParams, range) {
	const next = new URLSearchParams(searchParams);
	next.delete('range');
	next.delete('from');
	next.delete('to');
	const clean = normalizeRange(range);
	if (clean && clean.preset) next.set('range', clean.preset);
	else if (clean && clean.from) {
		next.set('from', clean.from);
		next.set('to', clean.to);
	}
	return next;
}

/** The words for a range: "Last 30 days", "Sep 1, 2026 to Sep 30, 2026", "All time" for none. */
export function rangeLabel(range) {
	if (range === null || range === undefined) return RANGE_PRESET_LABELS.all;
	if (isPreset(range.preset)) return RANGE_PRESET_LABELS[range.preset];
	if (isRangeDay(range.from) && isRangeDay(range.to)) {
		const from = formatDisplayDate(range.from) || range.from;
		const to = formatDisplayDate(range.to) || range.to;
		return from === to ? from : `${from} to ${to}`;
	}
	return RANGE_PRESET_LABELS.all;
}
